/**
 * Shared query key helpers.
 *
 * Domain hooks own their query key factories locally. This file keeps only
 * helper key factories for code paths that do not have a dedicated hook module.
 */

export const dialogDataKeys = {
  all: ["dialogData"] as const,
  checklists: ["dialogData", "checklists"] as const,
  positions: ["dialogData", "positions"] as const,
};

export const pdfProfilesKeys = {
  all: ["pdf-profiles"] as const,
  checklistProfiles: (checklistId: string) => ["checklist-pdf-profiles", checklistId] as const,
};

export const adminKeys = {
  tenants: ["admin-tenants"] as const,
  users: ["admin-all-users"] as const,
};

export const historyKeys = {
  all: ["audit-logs"] as const,
  filtered: (params: {
    userId?: string;
    activeTenantId?: string | null;
    canViewAll: boolean;
    search?: string;
    entityFilter?: string;
    userFilter?: string;
    showPermissionChangesOnly?: boolean;
    showUserStatusChangesOnly?: boolean;
    showSystemActionsOnly?: boolean;
    showSuperAdminActionsOnly?: boolean;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
  }) => ["audit-logs", params] as const,
};

export const breadcrumbKeys = {
  inspection: (inspectionId: string) => ["breadcrumb-inspection", inspectionId] as const,
};

export const rolePermissionsKeys = {
  all: ["role-permissions"] as const,
  assignability: ["role-assignability"] as const,
};

// TES-1765 — kanonische Rollen-Liste (roles-Tabelle, TES-1763-Fundament).
export const rolesKeys = {
  all: ["roles"] as const,
};

/**
 * TES-2027 — gemeinsame Query-Key-Domäne für Gruppen (team_group + group_members).
 *
 * Vor diesem Factory nutzten die Consumer VERSCHIEDENE Keys für dieselben Daten
 * (`["team-groups", …]` im TeamGroupsDialog vs. `["teamGroups", …]` in useTeamGroups
 * vs. `["groupMembers", …]` vs. `["group-effective-members", …]`), und Mutationen
 * invalidierten jeweils nur EINEN davon → Assignment-Picker blieben bis zu 5 min stale.
 * Die Einzel-Keys bleiben absichtlich unverändert (kein Ripple in bestehende Queries);
 * `invalidatePrefixes` bündelt alle Prefixe, die eine Gruppen-/Mitglieder-Mutation
 * gemeinsam refreshen muss.
 */
export const teamGroupKeys = {
  /** useTeamGroups — aktive Gruppen eines Teams für Zuweisungs-Picker. */
  lists: (teamId: string | null | undefined) => ["teamGroups", teamId] as const,
  /** TeamGroupsDialog — Verwaltungsliste (inkl. inaktiver Gruppen). */
  dialogList: (teamId: string) => ["team-groups", teamId] as const,
  /** useGroupMembers — effektive Mitglieder einer Gruppe (Bearbeiter-Picker). */
  members: (groupId: string | null | undefined) => ["groupMembers", groupId] as const,
  /** useEffectiveMembersPreview — Live-Vorschau der Regel-Auflösung im Group-Builder. */
  effectiveMembers: (groupId: string | null | undefined) =>
    ["group-effective-members", groupId] as const,
  /** useGroupEditLoad (TES-2096) — bestehende Gruppe für den Edit-Modus des Builders. */
  editLoad: (groupId: string | null | undefined) => ["group-edit-load", groupId] as const,
  /** Alle Prefixe, die eine Gruppen-/Mitglieder-Mutation gemeinsam invalidieren muss. */
  invalidatePrefixes: [
    ["teamGroups"],
    ["team-groups"],
    ["groupMembers"],
    ["group-effective-members"],
    ["group-edit-load"],
  ] as const,
};

/**
 * TES-2027 — invalidiert die gesamte Gruppen-Domäne nach einer Gruppen-/Mitglieder-Mutation,
 * damit ALLE Consumer (Verwaltungs-Dialog, Zuweisungs-Picker, Bearbeiter-Picker, Vorschau)
 * frisch sind, egal welchen Key sie nutzen. `client` ist der QueryClient.
 */
export function invalidateTeamGroupDomain(client: {
  invalidateQueries: (filter: { queryKey: readonly unknown[] }) => unknown;
}): void {
  for (const queryKey of teamGroupKeys.invalidatePrefixes) {
    client.invalidateQueries({ queryKey });
  }
}
